import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { InfoCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { paymentConfigService } from '../../services/paymentConfigService';
import { useAuth } from '../../contexts/AuthContext';
import { idsEqual, isAppScopedPlatform, toNumberId } from '../../utils/paymentConfig';
import { hasMenuPermission } from '../../utils/permissions';
import {
  PAYMENT_FORM_AUTO_COMPLETE,
  PAYMENT_INPUT_AUTO_COMPLETE,
  PaymentFormAutofillGuard,
} from './paymentFormAutocomplete';
import { showPaymentSaveWarnings } from './paymentSaveWarnings';
import {
  AppCredentialConfig,
  ClientPlatform,
  CLIENT_PLATFORM_MAP,
  MerchantConfig,
  PaymentProvider,
  PlatformPaymentConfig,
  PlatformPaymentMethod,
  PlatformPaymentRoute,
  PLATFORM_PAYMENT_METHOD_MAP,
} from '../../types';
import {
  PLATFORM_FILTER_OPTIONS,
  RouteFilters,
  RouteSelection,
  analyzeRouteScopeStatus,
  buildAppCredentialSelectOptions,
  buildMerchantSelectOptions,
  buildRouteFromSelection,
  buildRouteSavePayload,
  filterAppCredentialsForPlatform,
  filterPaymentRoutes,
  findPlatformConfig,
  flattenPlatformRoutes,
  formatRouteAppCredentialLabel,
  formatRouteMerchantLabel,
  getCompatibleRouteOptions,
  getEnableRuleAlertMessage,
  getProviderLabel,
  getRouteEnabledStatusLabel,
  isRouteConfigComplete,
  normalizeRouteSelection,
  resolveRouteScopeKey,
  sanitizeRouteSelection,
  sortPaymentRoutes,
  withExpectedActiveRouteId,
} from './platformPaymentRoutePage';

const { Title, Text } = Typography;

const ROUTE_TABLE_PAGE_SIZE = 20;

type ModalMode = 'create' | 'edit';

type RouteFormValues = {
  clientPlatform?: ClientPlatform;
  paymentMethod?: PlatformPaymentMethod;
  paymentProvider?: PaymentProvider;
  appCredentialId?: number;
  merchantConfigId?: number;
  enabled?: boolean;
  displayName?: string;
};

const PaymentChannelConfig: React.FC = () => {
  const { menus, permissions } = useAuth();
  const canSavePlatformConfig = hasMenuPermission(menus, permissions, 'PAYMENT_CONFIG', 'WRITE');

  const [platforms, setPlatforms] = useState<PlatformPaymentConfig[]>([]);
  const [allAppCredentials, setAllAppCredentials] = useState<AppCredentialConfig[]>([]);
  const [allMerchants, setAllMerchants] = useState<MerchantConfig[]>([]);
  const [filters, setFilters] = useState<RouteFilters>({});
  const [loading, setLoading] = useState(false);
  const [savingRoute, setSavingRoute] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>('create');
  const [editingRoute, setEditingRoute] = useState<PlatformPaymentRoute | undefined>();
  const [routeForm] = Form.useForm<RouteFormValues>();

  const paymentGlobals = useMemo(
    () => ({
      appCredentialConfigs: allAppCredentials,
      merchantConfigs: allMerchants,
    }),
    [allAppCredentials, allMerchants],
  );

  const allRoutes = useMemo(() => flattenPlatformRoutes(platforms), [platforms]);

  const routeScopeStatus = useMemo(
    () => analyzeRouteScopeStatus(allRoutes, allAppCredentials),
    [allAppCredentials, allRoutes],
  );

  const hasScopeConflicts =
    routeScopeStatus.conflictRouteIds.size > 0 || routeScopeStatus.emptyScopeRouteIds.size > 0;

  const filteredRoutes = useMemo(
    () => sortPaymentRoutes(filterPaymentRoutes(allRoutes, filters), allAppCredentials),
    [allRoutes, allAppCredentials, filters],
  );

  const credentialFilterOptions = useMemo(() => {
    const scoped = filterAppCredentialsForPlatform(allAppCredentials, filters.clientPlatform);
    return [
      { label: '全部', value: '' },
      ...buildAppCredentialSelectOptions(scoped).map(item => ({
        label: item.label,
        value: item.value,
      })),
    ];
  }, [allAppCredentials, filters.clientPlatform]);

  const merchantFilterOptions = useMemo(
    () => [
      { label: '全部', value: '' },
      ...buildMerchantSelectOptions(allMerchants).map(item => ({
        label: item.label,
        value: item.value,
      })),
    ],
    [allMerchants],
  );

  const modalPlatform = Form.useWatch('clientPlatform', routeForm);
  const modalPaymentMethod = Form.useWatch('paymentMethod', routeForm);
  const modalPaymentProvider = Form.useWatch('paymentProvider', routeForm);
  const modalAppCredentialId = Form.useWatch('appCredentialId', routeForm);
  const modalMerchantConfigId = Form.useWatch('merchantConfigId', routeForm);

  const modalPlatformConfig = useMemo(
    () => (modalPlatform ? findPlatformConfig(platforms, modalPlatform) : undefined),
    [modalPlatform, platforms],
  );

  const modalSelection = useMemo<RouteSelection>(
    () => ({
      paymentMethod: modalPaymentMethod,
      paymentProvider: modalPaymentProvider,
      appCredentialId: toNumberId(modalAppCredentialId),
      merchantConfigId: toNumberId(modalMerchantConfigId),
    }),
    [modalAppCredentialId, modalMerchantConfigId, modalPaymentMethod, modalPaymentProvider],
  );

  const modalRouteOptions = useMemo(
    () =>
      modalMode === 'edit' && editingRoute
        ? getCompatibleRouteOptions(
            findPlatformConfig(platforms, editingRoute.clientPlatform),
            normalizeRouteSelection(editingRoute),
            paymentGlobals,
          )
        : getCompatibleRouteOptions(modalPlatformConfig, modalSelection, paymentGlobals),
    [editingRoute, modalMode, modalPlatformConfig, modalSelection, paymentGlobals, platforms],
  );

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentConfigService.queryPaymentConfig();
      if (res.status === 1) {
        setPlatforms(res.data?.platforms || []);
        setAllAppCredentials(res.data?.allAppCredentialConfigs || []);
        setAllMerchants(res.data?.allMerchantConfigs || []);
      } else {
        message.error(res.msg || '获取支付配置失败');
      }
    } catch {
      message.error('获取支付配置失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const canEnableRoute = (route: PlatformPaymentRoute): { ok: boolean; reason?: string } => {
    if (!isRouteConfigComplete(route, paymentGlobals)) {
      return { ok: false, reason: '应用资质或商户/收款账号配置不完整，请先在参数配置页维护后再启用' };
    }
    const scopeKey = resolveRouteScopeKey(route, allAppCredentials);
    if (!scopeKey && isAppScopedPlatform(route.clientPlatform)) {
      return { ok: false, reason: '该路由作用域缺失，请先修复应用资质真实 AppID 后再启用' };
    }
    if (scopeKey) {
      const enabledInScope = allRoutes.filter(
        item => item.enabled && resolveRouteScopeKey(item, allAppCredentials) === scopeKey,
      );
      if (enabledInScope.length > 1) {
        return {
          ok: false,
          reason: '同一平台和真实 AppID 存在多条启用路由，请先停用冲突路由后再启用目标路由',
        };
      }
    }
    return { ok: true };
  };

  const persistRoute = async (route: PlatformPaymentRoute, successMessage: string) => {
    if (!canSavePlatformConfig) {
      message.warning('当前账号没有平台支付路由维护权限');
      return false;
    }
    if (route.enabled) {
      const gate = canEnableRoute(route);
      if (!gate.ok) {
        message.warning(gate.reason);
        return false;
      }
    }

    setSavingRoute(true);
    try {
      const payload = withExpectedActiveRouteId(route, allRoutes, allAppCredentials);
      const res = await paymentConfigService.saveRoutes([payload]);
      if (res.status === 1) {
        message.success(successMessage);
        showPaymentSaveWarnings(res.data);
        await loadConfig();
        return true;
      }
      message.error(res.msg || '平台支付路由保存失败');
      return false;
    } catch {
      message.error('平台支付路由保存失败');
      return false;
    } finally {
      setSavingRoute(false);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setEditingRoute(undefined);
    routeForm.resetFields();
    routeForm.setFieldsValue({
      clientPlatform: filters.clientPlatform,
      enabled: true,
      displayName: undefined,
    });
    setModalOpen(true);
  };

  const openEditModal = (route: PlatformPaymentRoute) => {
    setModalMode('edit');
    setEditingRoute(route);
    routeForm.setFieldsValue({
      clientPlatform: route.clientPlatform,
      paymentMethod: route.paymentMethod,
      paymentProvider: route.paymentProvider,
      appCredentialId: toNumberId(route.appCredentialId),
      merchantConfigId: toNumberId(route.merchantConfigId),
      enabled: route.enabled,
      displayName: route.displayName,
    });
    setModalOpen(true);
  };

  const handleModalPlatformChange = (platform: ClientPlatform) => {
    routeForm.setFieldsValue({
      clientPlatform: platform,
      paymentMethod: undefined,
      paymentProvider: undefined,
      appCredentialId: undefined,
      merchantConfigId: undefined,
    });
  };

  const handleModalMethodChange = (method: PlatformPaymentMethod) => {
    routeForm.setFieldsValue({
      paymentMethod: method,
      paymentProvider: undefined,
      appCredentialId: undefined,
      merchantConfigId: undefined,
    });
  };

  const handleModalProviderChange = (provider: PaymentProvider) => {
    routeForm.setFieldsValue({
      paymentProvider: provider,
      appCredentialId: undefined,
      merchantConfigId: undefined,
    });
  };

  const handleModalSave = async () => {
    try {
      const values = await routeForm.validateFields();
      if (modalMode === 'edit' && editingRoute) {
        const sanitized = sanitizeRouteSelection(
          {
            paymentMethod: editingRoute.paymentMethod,
            paymentProvider: editingRoute.paymentProvider,
            appCredentialId: values.appCredentialId,
            merchantConfigId: values.merchantConfigId,
          },
          allAppCredentials,
          allMerchants,
        );
        if (!sanitized.appCredentialId || !sanitized.merchantConfigId) {
          message.warning('请选择完整且合法的应用资质与商户/收款账号');
          return;
        }
        const payload = buildRouteSavePayload(
          editingRoute,
          {
            appCredentialId: sanitized.appCredentialId,
            merchantConfigId: sanitized.merchantConfigId,
            enabled: values.enabled ?? false,
            displayName: values.displayName?.trim() || undefined,
          },
          { allRoutes, appCredentials: allAppCredentials },
        );
        const saved = await persistRoute(payload, '路由已保存，后续新订单将按最新配置生效');
        if (saved) {
          setModalOpen(false);
        }
        return;
      }

      const platformConfig = values.clientPlatform
        ? findPlatformConfig(platforms, values.clientPlatform)
        : undefined;
      if (!platformConfig) {
        message.warning('请选择客户端平台');
        return;
      }
      const routePayload = buildRouteFromSelection(
        platformConfig,
        {
          paymentMethod: values.paymentMethod,
          paymentProvider: values.paymentProvider,
          appCredentialId: values.appCredentialId,
          merchantConfigId: values.merchantConfigId,
        },
        paymentGlobals,
        {
          enabled: values.enabled ?? true,
          displayName: values.displayName?.trim() || undefined,
        },
      );
      if (!routePayload) {
        message.warning('请选择完整且合法的平台、支付方式、服务商、应用资质与商户/收款账号组合');
        return;
      }
      const createPayload = withExpectedActiveRouteId(routePayload, allRoutes, allAppCredentials);
      const saved = await persistRoute(createPayload, '路由已保存，后续新订单将按最新配置生效');
      if (saved) {
        setModalOpen(false);
      }
    } catch {
      // validateFields failed
    }
  };

  const handleToggleEnabled = async (route: PlatformPaymentRoute, enabled: boolean) => {
    if (enabled) {
      const gate = canEnableRoute({ ...route, enabled: true });
      if (!gate.ok) {
        message.warning(gate.reason);
        return;
      }
    }
    await persistRoute(
      buildRouteSavePayload(route, { enabled }, { allRoutes, appCredentials: allAppCredentials }),
      enabled ? '路由已启用（配置已启用，不等于当前可执行）' : '路由已停用',
    );
  };

  const routeColumns = [
    {
      title: '配置启用状态',
      key: 'enabled',
      width: 130,
      render: (_: unknown, record: PlatformPaymentRoute) => {
        const status = getRouteEnabledStatusLabel(record, routeScopeStatus);
        return <Tag color={status.color}>{status.text}</Tag>;
      },
    },
    {
      title: '客户端平台',
      dataIndex: 'clientPlatform',
      key: 'clientPlatform',
      width: 130,
      render: (value: ClientPlatform) => CLIENT_PLATFORM_MAP[value] || value,
    },
    {
      title: '路由名称',
      dataIndex: 'displayName',
      key: 'displayName',
      width: 140,
      render: (value: string) => value || '-',
    },
    {
      title: '支付方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 150,
      render: (value: string) => PLATFORM_PAYMENT_METHOD_MAP[value as keyof typeof PLATFORM_PAYMENT_METHOD_MAP] || value,
    },
    {
      title: '支付服务商',
      dataIndex: 'paymentProvider',
      key: 'paymentProvider',
      width: 120,
      render: (value: string) => getProviderLabel(value),
    },
    {
      title: '应用资质',
      key: 'appCredential',
      width: 220,
      render: (_: unknown, record: PlatformPaymentRoute) => {
        const { label, invalid } = formatRouteAppCredentialLabel(record.appCredentialId, allAppCredentials);
        return <Text type={invalid ? 'danger' : undefined}>{label}</Text>;
      },
    },
    {
      title: '商户/收款账号',
      key: 'merchant',
      width: 220,
      render: (_: unknown, record: PlatformPaymentRoute) => {
        const { label, invalid } = formatRouteMerchantLabel(record.merchantConfigId, allMerchants);
        return <Text type={invalid ? 'danger' : undefined}>{label}</Text>;
      },
    },
    {
      title: '配置完整性',
      key: 'configComplete',
      width: 110,
      render: (_: unknown, record: PlatformPaymentRoute) => {
        const complete = isRouteConfigComplete(record, paymentGlobals);
        return <Tag color={complete ? 'green' : 'red'}>{complete ? '完整' : '待修复'}</Tag>;
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: unknown, record: PlatformPaymentRoute) => {
        const enableGate = canEnableRoute({ ...record, enabled: true });
        const enableBlockedReason = !canSavePlatformConfig
          ? '无保存权限'
          : !enableGate.ok
            ? enableGate.reason
            : '';
        return (
          <Space size="small">
            <Button size="small" onClick={() => openEditModal(record)}>
              编辑
            </Button>
            {record.enabled ? (
              <Button
                size="small"
                disabled={!canSavePlatformConfig}
                loading={savingRoute}
                onClick={() => handleToggleEnabled(record, false)}
              >
                停用
              </Button>
            ) : (
              <Tooltip title={enableBlockedReason}>
                <Button
                  size="small"
                  type="primary"
                  disabled={!canSavePlatformConfig || !enableGate.ok}
                  loading={savingRoute}
                  onClick={() => handleToggleEnabled(record, true)}
                >
                  启用
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const isCreateModal = modalMode === 'create';

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
            <Space align="center">
              <Title level={3} style={{ margin: 0 }}>
                平台支付路由配置
              </Title>
              <Tooltip title="路由表格为主视图；筛选仅影响展示。保存后仅影响新订单。">
                <InfoCircleOutlined style={{ color: '#1677ff' }} />
              </Tooltip>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={loadConfig} loading={loading}>
              刷新
            </Button>
          </Space>
          {!canSavePlatformConfig && (
            <Alert
              type="warning"
              showIcon
              message="当前账号没有平台支付路由维护权限，可查看但不可保存。"
              style={{ marginTop: 12 }}
            />
          )}
        </Card>

        <Card>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space wrap size="middle">
              <Space>
                <Text>客户端平台</Text>
                <Select
                  style={{ width: 160 }}
                  value={filters.clientPlatform || ''}
                  options={PLATFORM_FILTER_OPTIONS}
                  onChange={value => {
                    const platform = value ? (value as ClientPlatform) : undefined;
                    setFilters(prev => ({
                      ...prev,
                      clientPlatform: platform,
                      appCredentialId:
                        prev.appCredentialId !== undefined &&
                        allAppCredentials.some(
                          item =>
                            idsEqual(item.appCredentialId, prev.appCredentialId) &&
                            (!platform || item.clientPlatform === platform),
                        )
                          ? prev.appCredentialId
                          : undefined,
                    }));
                  }}
                />
              </Space>
              <Space>
                <Text>应用资质</Text>
                <Select
                  style={{ width: 280 }}
                  showSearch
                  optionFilterProp="label"
                  value={filters.appCredentialId ?? ''}
                  options={credentialFilterOptions}
                  onChange={value =>
                    setFilters(prev => ({
                      ...prev,
                      appCredentialId: value === '' ? undefined : Number(value),
                    }))
                  }
                />
              </Space>
              <Space>
                <Text>商户/收款账号</Text>
                <Select
                  style={{ width: 280 }}
                  showSearch
                  optionFilterProp="label"
                  value={filters.merchantConfigId ?? ''}
                  options={merchantFilterOptions}
                  onChange={value =>
                    setFilters(prev => ({
                      ...prev,
                      merchantConfigId: value === '' ? undefined : Number(value),
                    }))
                  }
                />
              </Space>
            </Space>

            <Alert type="info" showIcon message={getEnableRuleAlertMessage(filters.clientPlatform)} />
            <Alert
              type="info"
              showIcon
              message="云汇腾路由约束"
              description="云汇腾（YUNHUITENG）仅允许「微信小程序 + 微信小程序支付」组合；其他平台不会提供该服务商选项。"
            />
            {hasScopeConflicts && (
              <Alert
                type="error"
                showIcon
                message="检测到路由冲突或作用域缺失"
                description="同一平台和真实 AppID 存在多条启用路由，或启用路由缺少作用域。页面不会将任一冲突路由视为唯一生效路由；请先停用冲突路由并修复作用域后再启用目标路由。"
              />
            )}

            <Space>
              <Tooltip title={!canSavePlatformConfig ? '缺少平台支付配置保存权限' : ''}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!canSavePlatformConfig}
                  onClick={openCreateModal}
                >
                  新增路由
                </Button>
              </Tooltip>
            </Space>

            <Table
              rowKey={record =>
                record.routeId
                  ? String(record.routeId)
                  : `${record.clientPlatform}-${record.paymentMethod}-${record.paymentProvider}-${record.appCredentialId}-${record.merchantConfigId}`
              }
              columns={routeColumns}
              dataSource={filteredRoutes}
              pagination={{
                pageSize: ROUTE_TABLE_PAGE_SIZE,
                showSizeChanger: true,
                showTotal: total => `共 ${total} 条`,
              }}
              scroll={{ x: 1400 }}
              locale={{
                emptyText: '当前筛选下无路由，可调整筛选或新增路由',
              }}
            />
          </Space>
        </Card>
      </Space>

      <Modal
        title={isCreateModal ? '新增路由' : '编辑路由'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleModalSave}
        confirmLoading={savingRoute}
        okButtonProps={{ disabled: !canSavePlatformConfig }}
        destroyOnClose
        width={560}
      >
        {!isCreateModal && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="支付方式与支付服务商不可修改；若需变更请新增路由并停用旧路由。"
          />
        )}
        <Form form={routeForm} layout="vertical" preserve={false} autoComplete={PAYMENT_FORM_AUTO_COMPLETE}>
          <PaymentFormAutofillGuard />
          {isCreateModal ? (
            <Form.Item
              label="客户端平台"
              name="clientPlatform"
              rules={[{ required: true, message: '请选择客户端平台' }]}
            >
              <Select
                options={PLATFORM_FILTER_OPTIONS.filter(item => item.value !== '')}
                onChange={value => handleModalPlatformChange(value as ClientPlatform)}
              />
            </Form.Item>
          ) : (
            <Form.Item label="客户端平台">
              <Text>
                {editingRoute ? CLIENT_PLATFORM_MAP[editingRoute.clientPlatform] : '-'}
              </Text>
            </Form.Item>
          )}

          {isCreateModal ? (
            <>
              <Form.Item
                label="支付方式"
                name="paymentMethod"
                rules={[{ required: true, message: '请选择支付方式' }]}
              >
                <Select
                  options={modalRouteOptions.paymentMethods.map(method => ({
                    label: PLATFORM_PAYMENT_METHOD_MAP[method],
                    value: method,
                  }))}
                  onChange={value => handleModalMethodChange(value as PlatformPaymentMethod)}
                  disabled={!modalPlatform}
                  placeholder="请选择支付方式"
                />
              </Form.Item>
              <Form.Item
                label="支付服务商"
                name="paymentProvider"
                rules={[{ required: true, message: '请选择支付服务商' }]}
              >
                <Select
                  options={modalRouteOptions.paymentProviders.map(provider => ({
                    label: getProviderLabel(provider),
                    value: provider,
                  }))}
                  onChange={value => handleModalProviderChange(value as PaymentProvider)}
                  disabled={!modalPaymentMethod}
                  placeholder="请选择支付服务商"
                />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label="支付方式">
                <Text>
                  {editingRoute
                    ? PLATFORM_PAYMENT_METHOD_MAP[editingRoute.paymentMethod] || editingRoute.paymentMethod
                    : '-'}
                </Text>
              </Form.Item>
              <Form.Item label="支付服务商">
                <Text>{editingRoute ? getProviderLabel(editingRoute.paymentProvider) : '-'}</Text>
              </Form.Item>
            </>
          )}

          <Form.Item
            label="应用资质"
            name="appCredentialId"
            rules={[{ required: true, message: '请选择应用资质' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              optionLabelProp="label"
              options={buildAppCredentialSelectOptions(modalRouteOptions.appCredentialConfigs)}
              disabled={isCreateModal && (!modalPaymentProvider || !modalPlatform)}
              placeholder="请选择应用资质"
            />
          </Form.Item>
          <Form.Item
            label="商户/收款账号"
            name="merchantConfigId"
            rules={[{ required: true, message: '请选择商户/收款账号' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              optionLabelProp="label"
              options={buildMerchantSelectOptions(modalRouteOptions.merchantConfigs)}
              disabled={isCreateModal && (!modalPaymentProvider || !modalPlatform)}
              placeholder="请选择商户/收款账号"
            />
          </Form.Item>
          <Form.Item
            label="是否启用（配置已启用）"
            name="enabled"
            valuePropName="checked"
            extra="仅表示配置已启用，不等于当前生产可执行；可执行状态取决于服务商运行门禁。"
          >
            <Switch />
          </Form.Item>
          <Form.Item label="路由名称" name="displayName">
            <Input autoComplete={PAYMENT_INPUT_AUTO_COMPLETE} placeholder="可选，便于运营识别" allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
};

export default PaymentChannelConfig;

// Re-export for tests (legacy import path)
export {
  ROUTE_EDIT_LOCKED_FIELDS,
  buildRouteFromSelection,
  filterPaymentRoutes,
  getCompatibleRouteOptions,
  isRouteConfigComplete,
  normalizeRouteSelection,
  sanitizeRouteSelection,
} from './platformPaymentRoutePage';
